import * as fns from "date-fns"
import { fr } from "date-fns/locale"

fns.setDefaultOptions({ locale: fr })

export class DateModel {
    static formatShort(date: Date | string) {
        return this.createFormatter("d MMMM", date)
    }
    static formatDate(date: Date | string) {
        return this.createFormatter("dd/MM/yyyy", date)
    }

    static formatDatePicker(date: Date | string) {
        return this.createFormatter("yyyy-MM-dd", date)
    }

    static formatApi(date: Date | string) {
        return this.createFormatter("yyyy-MM-dd HH:mm", date)
    }

    static formatDateTime(date: Date | string) {
        return this.createFormatter("dd/MM/yyyy HH:mm", date)
    }

    static formatTime(date: Date | string) {
        return this.createFormatter("HH:mm", date)
    }

    static formatPlainDate(date: Date | string) {
        return this.createFormatter("dd MMMM yyyy", date)
    }

    static formatFullDate(date: Date | string) {
        return this.createFormatter("dd MMMM yyyy à HH:mm", date)
    }

    static formatMounthYear(date: Date | string) {
        return this.createFormatter("MMMM yyyy", date)
    }

    static formatTitleCalendar(date: Date | string) {
        return this.createFormatter("MMMM yyyy - w", date)
    }

    static formatCompleteDay(date: Date | string) {
        return this.createFormatter("eeee d MMMM", date)
    }

    static formatYear(date: Date | string) {
        return this.createFormatter("yyyy", date)
    }

    static formatLetter(date: Date | string) {
        return this.createFormatter("iiiiii", date)
    }

    static createFormatter(formatStr: string, date: Date | string) {
        const dateObj = this._validateAndConvert(date)
        return fns.format(dateObj, formatStr)
    }

    static get today() {
        return new Date()
    }

    static get startOfDay() {
        return fns.startOfDay(this.today)
    }

    static get endOfDay() {
        return fns.endOfDay(this.today)
    }

    static get startOfWeek() {
        return fns.startOfWeek(this.today)
    }

    static get endOfWeek() {
        return fns.endOfWeek(this.today)
    }

    static get startOfMonth() {
        return fns.startOfMonth(this.today)
    }

    static get endOfMonth() {
        return fns.endOfMonth(this.today)
    }

    static get startOfLastMonth() {
        return fns.startOfMonth(fns.subMonths(new Date(), 1))
    }

    static get endOfLastMonth() {
        return fns.addDays(fns.endOfMonth(fns.subMonths(new Date(), 1)), 1)
    }

    static get startOfNextMonth() {
        return fns.addMonths(DateModel.startOfMonth, 1)
    }

    static get endOfNextMonth() {
        return fns.addMonths(DateModel.endOfMonth, 1)
    }

    static get startOf3MonthsAgo() {
        return fns.startOfMonth(fns.subMonths(new Date(), 3))
    }

    static get startOf7DaysAgo() {
        return fns.startOfDay(fns.subDays(new Date(), 7))
    }

    static get startOf30DaysAgo() {
        return fns.startOfDay(fns.subDays(new Date(), 30))
    }

    static get lastWeekStart() {
        return fns.startOfWeek(fns.subWeeks(this.today, 1))
    }

    static get lastWeekEnd() {
        return fns.endOfWeek(fns.subWeeks(this.today, 1))
    }

    static get startOfYear() {
        return fns.startOfYear(this.today)
    }

    static get endOfYear() {
        return fns.endOfYear(this.today)
    }

    static get nextWeekStart() {
        return fns.startOfWeek(fns.addWeeks(this.today, 1))
    }

    static get nextWeekEnd() {
        return fns.endOfWeek(fns.addWeeks(this.today, 1))
    }

    static formatISO(date) {
        return fns.formatISO(date ? date : new Date())
    }

    static weekNumber(date) {
        return fns.getISOWeek(date)
    }

    static createIsoFromDateTime(date = new Date(), time) {
        const [hours, minutes] = time.split(":")

        const dateWithTime = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            parseInt(hours, 10),
            parseInt(minutes, 10),
        )
        return fns.format(dateWithTime, "yyyy-MM-dd HH:mm:ss")
    }

    static isSameMonthAndYear(date1, date2) {
        return (
            date1 &&
            date2 &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear()
        )
    }

    static isSameDate(date1, date2) {
        return fns.isEqual(fns.startOfDay(date1), fns.startOfDay(date2))
    }

    static parseISO(date: string | Date): Date {
        if (date instanceof Date) return date
        return fns.parseISO(date)
    }

    static _validateAndConvert(input) {
        let dateObj = new Date(input)

        if (fns.isValid(dateObj)) {
            return dateObj
        }
        // Essaie de reformater si la chaîne est dans le format 'YYYYMMDD'
        if (/^\d{8}$/.test(input)) {
            const year = input.substring(0, 4)
            const month = input.substring(4, 6)
            const day = input.substring(6, 8)
            dateObj = fns.parseISO(`${year}-${month}-${day}`)

            if (fns.isValid(dateObj)) {
                return dateObj
            }
        }
        throw new Error("Invalid date")
    }

    static differenceInDays(date1, date2) {
        return fns.differenceInDays(new Date(date1), new Date(date2))
    }

    static differenceInHours(date1, date2) {
        return fns.differenceInHours(new Date(date1), new Date(date2))
    }

    static formatDifference(start, end) {
        const startDate = new Date(start)
        const endDate = new Date(end)

        const daysDifference = Math.ceil(
            (endDate - startDate) / (1000 * 60 * 60 * 24),
        )

        const totalHoursDifference = Math.floor(
            (endDate - startDate) / (1000 * 60 * 60),
        )
        const hoursDifference = totalHoursDifference % 24

        if (daysDifference > 1) return daysDifference + "j"
        if (hoursDifference) return hoursDifference + "h"
        return "Aucun"
    }

    static createDateTime(date, time) {
        // Si la date est au format 'YYYY-MM-DD' et l'heure au format 'HH:mm' sinon retourn une erreur
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
            throw new Error("Invalid date or time")
        }

        return fns.parseISO(date + "T" + time)
    }

    static getFistDayOfLastMonth() {
        return fns.startOfMonth(fns.subMonths(new Date(), 1))
    }

    static addHours(date, hours) {
        return fns.addHours(date, hours)
    }

    static hourMinuteFromDateTime(date) {
        // use fns.parseISO to convert date to a valid date object
        if (typeof date === "string") date = fns.parseISO(date)

        const time = fns.format(date, "HH:mm")
        return {
            hour: time.split(":")[0],
            minute: time.split(":")[1],
        }
    }

    static dateTimeFromDateAndTime(date, time) {
        const dateTime = fns.set(date, {
            hours: time.hour,
            minutes: time.minute,
            seconds: 0,
            milliseconds: 0,
        })

        return fns.formatISO(dateTime)
    }

    static displayDialogViewTitleCalendar(start, end) {
        const startDate = DateModel._validateAndConvert(start)
        const endDate = DateModel._validateAndConvert(end)

        return (
            fns.format(startDate, "dd/MM/yyyy") +
            " de " +
            fns.format(startDate, "HH:mm") +
            " à " +
            fns.format(endDate, "HH:mm")
        )
    }

    static adjustDates(startDate, endDate) {
        let start = new Date(startDate)
        let end = new Date(endDate)

        if (fns.differenceInHours(end, start) < 1) {
            end = fns.addHours(start, 1)
        }
        return { adjustedStartDate: start, adjustedEndDate: end }
    }

    static isBetween(date, startDate, endDate) {
        return fns.isWithinInterval(this.parseISO(date), {
            start: this.parseISO(startDate),
            end: this.parseISO(endDate),
        })
    }

    static isBefore(date, startDate) {
        return fns.isBefore(fns.parseISO(date), fns.parseISO(startDate))
    }

    static isAfter(date, startDate) {
        return fns.isAfter(fns.parseISO(date), fns.parseISO(startDate))
    }

    static isInterval(startDate, endDate, startDateInterval, endDateInterval) {
        try {
            if (!startDate || !endDate) return false
            if (!endDateInterval) endDateInterval = startDateInterval

            startDate = new Date(startDate)
            endDate = new Date(endDate)
            startDateInterval = new Date(startDateInterval)
            endDateInterval = new Date(endDateInterval)

            return !(
                fns.isBefore(endDateInterval, startDate) ||
                fns.isAfter(startDateInterval, endDate)
            )
        } catch (e) {
            return false
        }
    }
}
